import { useSelector } from "react-redux";
import { usePermissions } from "src/grading/hooks/usePermissions";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { Permission } from "src/models";
import { StaffRole, grantableRolesFor } from "src/models/staff";
import {
  selectCurrentScopes,
  selectCurrentUser,
} from "src/store/reducers/auth/authSlice";
import { useListSchoolsQuery } from "src/store/reducers/districts/districtsApiSlice";

/** The viewer's own role, from the signed-in profile or, before it loads, from the permissions. */
const callerRolesFrom = (
  roles: readonly string[] | undefined,
  has: (permission: Permission) => boolean,
): string[] => {
  if (roles?.length) return [...roles];
  if (has(permissions.READ_DISTRICTS)) return ["platform_admin"];
  if (has(permissions.UPDATE_OWN_DISTRICT)) return ["district_admin"];
  if (has(permissions.UPDATE_STAFF) || has(permissions.INVITE_STAFF)) {
    return ["school_admin"];
  }
  return [];
};

/**
 * Who is looking and what they can reach: the district the lists cover, the schools they can
 * filter by and the roles they can grant. The server re-checks every request.
 *
 * @param district the platform admin's chosen district (the URL's `district`).
 */
export const usePeopleScope = (district?: string) => {
  const { hasPermissions } = usePermissions();
  const has = (permission: Permission) =>
    hasPermissions(permission.action, permission.subject);
  const user = useSelector(selectCurrentUser);
  const scopes = useSelector(selectCurrentScopes);

  const isPlatformAdmin = has(permissions.READ_DISTRICTS);
  /** Sent on list requests; only platform admins name a district, the server scopes the rest. */
  const requestDistrictId = isPlatformAdmin ? district : undefined;
  const districtId = isPlatformAdmin ? district : scopes?.districtId;
  const canListSchools = isPlatformAdmin || has(permissions.READ_SCHOOLS);
  const { data: schools } = useListSchoolsQuery(districtId ?? "", {
    skip: !canListSchools || !districtId,
  });

  const grantableRoles: StaffRole[] = grantableRolesFor(
    callerRolesFrom(user?.roles, has),
  );

  return {
    has,
    isPlatformAdmin,
    districtId,
    requestDistrictId,
    /** Schools the viewer can pick; empty for school admins, who only see their own. */
    schools: canListSchools ? (schools ?? []) : [],
    /** A school admin's own school, used where a school is required. */
    ownSchoolId: canListSchools ? undefined : scopes?.schoolIds?.[0],
    grantableRoles,
    selfId: user?.id,
  };
};

export default usePeopleScope;
